/**
 * Browser-side wrapper around Clover's official `remote-pay-cloud` SDK for
 * talking directly to a Clover terminal on the store's local Wi-Fi
 * (Secure Network Pay Display / CloverConnector over WebSocket, port 12345).
 *
 * The gateway backend can't reach a private LAN IP from the cloud, so it
 * hands the browser a "CLOVER_WS" dispatch descriptor (uri/applicationId/
 * posName/serialNumber/authToken) and THIS browser - which is on the same
 * Wi-Fi as the terminal - makes the actual connection and sends the sale.
 *
 * On first use for a given terminal there's a one-time pairing step: the
 * terminal shows a pairing code, the SDK exchanges it for a persistent auth
 * token, and `onPairingSuccess` is called with that token so the caller can
 * save it (via the backend PATCH endpoint) for reuse on future runs.
 */
import * as sdk from "remote-pay-cloud";

/**
 * Runs one sale against a Clover terminal over the local WebSocket protocol.
 *
 * @param {Object} opts
 * @param {string} opts.uri - wss://<ip>:12345/remote_pay
 * @param {string} opts.applicationId
 * @param {string} opts.posName
 * @param {string} opts.serialNumber
 * @param {string|null} opts.authToken - previously saved pairing token, if any
 * @param {number} opts.amountCents
 * @param {string} opts.externalId
 * @param {(code:string)=>void} [opts.onPairingCode] - called with the code to show the user
 * @param {(token:string)=>void} [opts.onAuthToken] - called with the token once pairing succeeds, so the caller can persist it
 * @param {(status:string)=>void} [opts.onStatus] - called with human-readable progress updates
 * @param {number} [opts.timeoutMs]
 * @returns {Promise<Object>} the SaleResponse from the SDK
 */
export function runCloverLocalSale(opts) {
  const {
    uri,
    applicationId,
    posName,
    serialNumber,
    authToken,
    amountCents,
    externalId,
    onPairingCode,
    onAuthToken,
    onStatus,
    timeoutMs = 60000,
  } = opts;

  const status = (msg) => {
    if (onStatus) onStatus(msg);
  };

  return new Promise((resolve, reject) => {
    let settled = false;
    let saleSent = false;
    let cloverConnector = null;

    const finish = (fn, arg) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try {
        if (cloverConnector) cloverConnector.dispose();
      } catch (e) {
        // ignore
      }
      fn(arg);
    };

    const timer = setTimeout(() => {
      finish(
        reject,
        new Error(
          "Timed out waiting for the terminal. Make sure Network Pay Display shows " +
            '"Started" (not "not yet started") on the device, the URI matches exactly, ' +
            "and this device is on the same Wi-Fi as the terminal."
        )
      );
    }, timeoutMs);

    try {
      const configBuilder = new sdk.WebSocketPairedCloverDeviceConfigurationBuilder(
        applicationId,
        uri,
        posName,
        serialNumber,
        authToken || undefined,
        (pairingCode) => {
          status(`Pairing code: ${pairingCode} — confirm/enter this on the terminal screen.`);
          if (onPairingCode) onPairingCode(pairingCode);
        },
        (newAuthToken) => {
          status("Pairing succeeded.");
          if (onAuthToken) onAuthToken(newAuthToken);
        }
      );
      const deviceConfig = configBuilder.build();

      // CloverConnectorFactoryBuilder has no `.build()` instance method (a
      // common assumption from other Clover SDK versions) - the real v4.x
      // API is the static `createICloverConnectorFactory`. Calling it with
      // no FACTORY_VERSION returns the legacy (1.1.0) factory, which is
      // built for Clover's OAuth/cloud browser flow and requires a
      // clientId/domain we don't have for local pairing ("Both 'clientId'
      // and 'domain' are unset"). VERSION_12 is the one the SDK's own docs
      // describe as "not browser dependant... capable of a direct
      // connection to the device via the Network Pay Display app" - exactly
      // our local WebSocket + pairing case.
      const factory = sdk.CloverConnectorFactoryBuilder.createICloverConnectorFactory({
        [sdk.CloverConnectorFactoryBuilder.FACTORY_VERSION]: sdk.CloverConnectorFactoryBuilder.VERSION_12,
      });
      cloverConnector = factory.createICloverConnector(deviceConfig);

      const listener = new sdk.remotepay.ICloverConnectorListener();

      listener.onDeviceConnected = () => status("Connected to terminal (transport level)...");

      listener.onDeviceReady = () => {
        status("Terminal ready — sending sale request...");
        if (saleSent) return;
        saleSent = true;
        const saleRequest = new sdk.remotepay.SaleRequest();
        saleRequest.setExternalId(externalId);
        saleRequest.setAmount(amountCents);
        cloverConnector.sale(saleRequest);
      };

      listener.onDeviceDisconnected = () => status("Terminal disconnected.");

      listener.onDeviceError = (deviceErrorEvent) => {
        finish(reject, new Error(deviceErrorEvent?.message || "Terminal reported an error."));
      };

      listener.onSaleResponse = (response) => {
        if (response && typeof response.getSuccess === "function" && response.getSuccess()) {
          finish(resolve, response);
        } else {
          finish(reject, new Error("Terminal did not report a successful sale."));
        }
      };

      cloverConnector.addCloverConnectorListener(listener);
      status(`Connecting to ${uri} ...`);
      cloverConnector.initializeConnection();
    } catch (err) {
      finish(reject, err);
    }
  });
}
